import { useState } from 'react';
import { CarteIncidents } from '../pages/incidents/CarteIncidents';
import { useQuery } from '@tanstack/react-query';
import { CarteTerritoire, centreEmprise, emprise, type MarqueurCarte } from '@eec/ui';
import type { MissionTerrainReseau } from '@eec/contrats';
import { lireLimites } from '../pages/incidents/CarteEquipements';

export function CarteMissionsTerrain({ missions }: { missions: MissionTerrainReseau[] }) {
  const [zoom, setZoom] = useState('gabon');
  const limites = useQuery({
    queryKey: ['limites-cartographiques-gabon'],
    queryFn: ({ signal }) => lireLimites(signal),
    staleTime: Infinity,
    networkMode: 'always',
  });
  const groupes = new Map<string, { nom: string; nombre: number }>();
  for (const m of missions) {
    if (!m.province_code) continue;
    const g = groupes.get(m.province_code) ?? { nom: m.province ?? m.province_code, nombre: 0 };
    g.nombre++;
    groupes.set(m.province_code, g);
  }
  const echelles = limites.data?.filter((t) => t.id === 'gabon' || groupes.has(t.id)) ?? [];
  const courante = echelles.some((t) => t.id === zoom) ? zoom : 'gabon';
  const marqueurs: MarqueurCarte[] = [...groupes].flatMap(([id, g], i) => {
    const t = echelles.find((t) => t.id === id);
    if (!t?.geometrie) return [];
    const [longitude, latitude] = centreEmprise(emprise(t.geometrie));
    return [
      {
        id,
        numero: i + 1,
        longitude,
        latitude,
        libelle: `${g.nom} : ${g.nombre} mission(s) téléchargée(s)`,
        ton: 'autre',
      },
    ];
  });
  return (
    <>
      <CarteIncidents
        missions
        incidents={missions.map((m) => ({
          id: m.id,
          titre: m.titre,
          reference: m.incident_reference,
          statut: m.statut,
          latitude: m.latitude ?? null,
          longitude: m.longitude ?? null,
        }))}
      />
    <section aria-label="Carte des missions terrain" className="eec-carte">
      <h2>Missions téléchargées par province</h2>
      <p>
        Les repères représentent les provinces des missions téléchargées, pas leur position exacte.
        Les limites sont indicatives. Cette carte ne fournit pas de guidage routier.
      </p>
      {limites.error && (
        <p role="alert">Carte indisponible ; les missions restent consultables ci-dessous.</p>
      )}
      {limites.isPending && <p>Chargement de la carte…</p>}
      {echelles.length > 0 && (
        <CarteTerritoire
          titre="Missions terrain au Gabon"
          echelles={echelles}
          echelle={courante}
          onEchelle={setZoom}
          marqueurs={marqueurs}
          onMarqueur={setZoom}
          marqueurActif={courante === 'gabon' ? null : courante}
          fond={null}
          hauteur={360}
          surfaces={{
            type: 'FeatureCollection',
            features: echelles
              .filter(
                (t) =>
                  t.niveau === 'province' &&
                  t.geometrie &&
                  (courante === 'gabon' || t.id === courante),
              )
              .map((t) => ({
                type: 'Feature',
                properties: { libelle: t.libelle },
                geometry: t.geometrie!,
              })),
          }}
          libellesTon={{
            autre: 'Avec mission téléchargée',
            ouvert: 'Avec mission téléchargée',
            retard: 'Avec mission téléchargée',
          }}
          libelleObjets={{ singulier: 'province avec mission', pluriel: 'provinces avec mission' }}
        />
      )}
      {limites.data && marqueurs.length !== groupes.size && (
        <p role="alert">
          Certaines provinces ne peuvent pas être placées sur la carte. Consultez la liste.
        </p>
      )}
      {missions.some((m) => !m.province_code) && (
        <p>
          Des missions de cet ancien coffre n’ont pas de province. Rechargez les missions une fois
          en ligne pour compléter la carte.
        </p>
      )}
      {missions.length === 0 && <p>Aucune mission téléchargée.</p>}
      <ul aria-label="Nombre de missions par province">
        {[...groupes].map(([id, g]) => (
          <li key={id}>
            {g.nom} : {g.nombre} mission(s) téléchargée(s)
          </li>
        ))}
      </ul>
    </section>
    </>
  );
}
